import React, { FunctionComponent, useState, useEffect } from "react"
import { Icon, ModalAlert } from "@kuali/pure-ui"

interface Props {
  message: string
  title: string
  color?: string
  iconName?: string
}

/*
  ModaAlert is a variation of the `<Modal>` `pure-ui` component - per the docs
  A modal alert conveys a prominent message and any related actions. 
  Modal alerts support a title and up to two buttons as action items. 
  This particular version handles user confirmation when navigating away from page with unsanved changes.
  https://kualico.github.io/pure-ui/docs-alert#modal-alert
*/
const ReactExitConfirmationModalAlert: FunctionComponent<Props> = ({
  message,
  title,
  color = "red",
  iconName = "alert-error"
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [intendedTarget, setIntendedTarget] = useState()
  const [changeCallback, setChangeCallback] = useState(null)

  useEffect(() => {
    // listen for dirty changes in page that would trigger confirmation before they navigate
    // elsewhere - custom event needed so React component can always subscribe to event emitter
    // even in legacy JS
    window.addEventListener("dirtyChange", function(event: CustomEvent) {
      if (event.detail && event.detail.isDirty) {
        const isFormExitedDirty = event.detail.isDirty

        if (event.detail.intendedTarget) {
          // set intended URL target user was trying to go to
          setIntendedTarget(event.detail.intendedTarget)
        }

        if (event.detail.changeCallback) {
          // assign function to be invoked with provided callback in event data
          const eventCallback = () => {
            return event.detail.changeCallback
          }
          setChangeCallback(eventCallback)
        }

        if (isFormExitedDirty) {
          setIsOpen(true)
        }
      }
    })
  }, [])

  return (
    <React.Fragment>
      <div id="detect-change-modal" data-react-track-changes="post">
        <ModalAlert
          id={"primary-alert"}
          color={color}
          icon={<Icon name={iconName} />}
          title={title}
          message={message}
          primaryButtonLabel={"Leave"}
          closeButtonLabel={"Stay"}
          onPrimaryButtonClicked={(e) => {
            if (intendedTarget) {
              // let user continue despite unsaved changes
              // NOTE: not recommended to handle url reassignment with JS, this is in
              // place to handle legacy navigation rules for now
              window.changeGuard.clearDirty()
              window.location.assign(intendedTarget)
            } else if (changeCallback) {
              // invoke callback when primary button has been clicked
              changeCallback()
            }

            setIsOpen(false)
          }}
          onCloseButtonClicked={(e) => {
            setIsOpen(false)
          }}
          isOpen={isOpen}
        />
      </div>
    </React.Fragment>
  )
}
export default ReactExitConfirmationModalAlert
